import { useCallback, useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ArrowLeft, Minus, Plus, RotateCcw } from 'lucide-react-native';
import { KnowledgeGraphCanvas } from '../components/business/KnowledgeGraphCanvas';
import { KnowledgeGraphDetailSheet } from '../components/business/KnowledgeGraphDetailSheet';
import { AppButton } from '../components/ui/AppButton';
import { AppText } from '../components/ui/AppText';
import { ScreenBackground } from '../components/ui/ScreenBackground';
import { tokens } from '../theme/tokens';
import type {
  KnowledgeGraphEdgeViewModel,
  KnowledgeGraphNodeViewModel,
} from '../types/learningAnalysis';

const fullscreenNodes: KnowledgeGraphNodeViewModel[] = [
  {
    id: 'function',
    label: '二次函数应用',
    subject: '数学',
    masteryPercent: 58,
    statusLabel: '薄弱',
    status: 'weak',
    description: '最值、取值范围和实际应用题容易漏条件，建议先复盘本周错题。',
    actionLabel: '练函数专项',
  },
  {
    id: 'geometry',
    label: '几何证明',
    subject: '数学',
    masteryPercent: 72,
    statusLabel: '待复习',
    status: 'review',
    description: '证明链条基本完整，但中间条件书写不够稳定。',
    actionLabel: '复盘证明题',
  },
  {
    id: 'buoyancy',
    label: '浮力计算',
    subject: '物理',
    masteryPercent: 64,
    statusLabel: '待复习',
    status: 'review',
    description: '单位换算和受力分析需要和函数应用一起做迁移练习。',
    actionLabel: '练浮力计算',
  },
  {
    id: 'cloze',
    label: '完形语境',
    subject: '英语',
    masteryPercent: 84,
    statusLabel: '已掌握',
    status: 'mastered',
    description: '语境判断稳定，可以保持低频复习。',
    actionLabel: '看巩固卡片',
  },
  {
    id: 'formula',
    label: '公式变形',
    subject: '数学',
    masteryPercent: 66,
    statusLabel: '待复习',
    status: 'review',
    description: '公式变形和函数应用、浮力计算都有强关联，适合放在同一组训练。',
    actionLabel: '练公式变形',
  },
];

const fullscreenEdges: KnowledgeGraphEdgeViewModel[] = [
  { id: 'function-geometry', sourceId: 'function', targetId: 'geometry', strengthLabel: '同学科' },
  { id: 'function-buoyancy', sourceId: 'function', targetId: 'buoyancy', strengthLabel: '建模迁移' },
  { id: 'function-formula', sourceId: 'function', targetId: 'formula', strengthLabel: '核心前置' },
  { id: 'geometry-formula', sourceId: 'geometry', targetId: 'formula', strengthLabel: '步骤关联' },
  { id: 'cloze-formula', sourceId: 'cloze', targetId: 'formula', strengthLabel: '低频复盘' },
];

export default function KnowledgeGraphPage() {
  const router = useRouter();
  const [zoomLevel, setZoomLevel] = useState(1);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(fullscreenNodes[0]?.id ?? null);
  const selectedNode = fullscreenNodes.find((node) => node.id === selectedNodeId) ?? null;

  const zoomIn = useCallback(() => {
    setZoomLevel((current) => Math.min(1.22, Number((current + 0.1).toFixed(2))));
  }, []);

  const zoomOut = useCallback(() => {
    setZoomLevel((current) => Math.max(0.82, Number((current - 0.1).toFixed(2))));
  }, []);

  const resetZoom = useCallback(() => {
    setZoomLevel(1);
  }, []);

  const openPractice = useCallback(() => {
    router.push('/wrong-question-practice');
  }, [router]);

  return (
    <SafeAreaView style={styles.screen}>
      <ScreenBackground style={styles.background}>
        <View style={styles.header}>
          <AppButton
            label="轨迹"
            variant="ghost"
            onPress={() => router.back()}
            leftSlot={<ArrowLeft size={18} color={tokens.colors.primary} />}
            style={styles.backButton}
          />
          <View style={styles.headerCopy}>
            <AppText variant="title" weight="bold">
              知识图谱全屏
            </AppText>
            <AppText variant="caption" tone="muted">
              查看知识点关系、薄弱节点和推荐练习
            </AppText>
          </View>
        </View>

        <ScrollView contentContainerStyle={styles.content}>
          <View style={styles.toolbar}>
            <AppButton
              label="缩小"
              variant="secondary"
              onPress={zoomOut}
              leftSlot={<Minus size={16} color={tokens.colors.primary} />}
              style={styles.toolButton}
            />
            <AppText variant="caption" tone="muted" weight="bold" style={styles.zoomLabel}>
              {Math.round(zoomLevel * 100)}%
            </AppText>
            <AppButton
              label="放大"
              variant="secondary"
              onPress={zoomIn}
              leftSlot={<Plus size={16} color={tokens.colors.primary} />}
              style={styles.toolButton}
            />
            <AppButton
              label="重置"
              variant="ghost"
              onPress={resetZoom}
              leftSlot={<RotateCcw size={16} color={tokens.colors.primary} />}
              style={styles.toolButton}
            />
          </View>

          <KnowledgeGraphCanvas
            nodes={fullscreenNodes}
            edges={fullscreenEdges}
            selectedNodeId={selectedNodeId}
            onSelectNode={(node) => setSelectedNodeId(node.id)}
            height={360}
            scale={zoomLevel}
          />

          <KnowledgeGraphDetailSheet
            node={selectedNode}
            onClose={() => setSelectedNodeId(null)}
            onAction={openPractice}
          />
        </ScrollView>
      </ScreenBackground>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: tokens.colors.background,
  },
  background: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.md,
    paddingHorizontal: tokens.layout.screenPadding,
    paddingTop: tokens.spacing.md,
    paddingBottom: tokens.spacing.sm,
  },
  backButton: {
    minHeight: 40,
    paddingHorizontal: tokens.spacing.sm,
  },
  headerCopy: {
    flex: 1,
  },
  content: {
    paddingHorizontal: tokens.layout.screenPadding,
    paddingTop: tokens.spacing.md,
    paddingBottom: tokens.spacing.xxl,
    gap: tokens.spacing.lg,
  },
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
  },
  toolButton: {
    minHeight: 38,
    paddingHorizontal: tokens.spacing.md,
  },
  zoomLabel: {
    minWidth: 42,
    textAlign: 'center',
  },
});
